// src/pages/Admin/Users.js
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import adminApi from '../../services/adminApi';
import styles from './Users.module.css';
import UserCoinManager from './UserCoinManager';
import { OnboardingJourney } from './OnboardingDiagnosticsPanel';

export default function Users() {
    const { user: currentUser } = useAuth();
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        role: '',
        search: '',
        status: 'all',
        page: 1
    });
    const [pagination, setPagination] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);

    const isSuperAdmin = currentUser?.role === 'super_admin';
    console.log('Current user role:', currentUser?.role);

    useEffect(() => {
        fetchUsers();
    }, [filters]);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const data = await adminApi.listUsers(filters);
            setUsers(data.users);
            setPagination(data.pagination);
        } catch (err) {
            console.error('Failed to load users:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleFoundingToggle = async (userId, granted) => {
        const verb = granted ? 'Grant' : 'Revoke';
        if (!window.confirm(`${verb} the Founding Collector badge for this user? This is a cosmetic status label only.`)) {
            return;
        }
        try {
            const result = await adminApi.setFoundingCollector(userId, granted);
            const patch = {
                is_founding_collector: result.is_founding_collector,
                isFoundingCollector: result.isFoundingCollector,
                founding_collector_granted_at: result.founding_collector_granted_at,
            };
            setUsers(prev => prev.map(u => (u.id === userId ? { ...u, ...patch } : u)));
            setSelectedUser(prev => (prev && prev.user?.id === userId
                ? { ...prev, user: { ...prev.user, ...patch } }
                : prev));
        } catch (err) {
            console.error('Failed to update Founding Collector status:', err);
            alert(err.message || 'Failed to update Founding Collector status');
        }
    };

    const handleViewUser = async (userId) => {
        try {
            const data = await adminApi.getUserDetail(userId);
            setSelectedUser(data);
        } catch (err) {
            console.error('Failed to load user detail:', err);
        }
    };

    const handleRoleChange = async (userId, newRole) => {
        if (!isSuperAdmin) {
            alert('Only super admins can change roles');
            return;
        }

        if (!window.confirm(`Are you sure you want to change this user's role to ${newRole}?`)) {
            return;
        }

        try {
            await adminApi.updateUserRole(userId, newRole);
            fetchUsers();
            if (selectedUser?.user?.id === userId) {
                const data = await adminApi.getUserDetail(userId);
                setSelectedUser(data);
            }
        } catch (err) {
            console.error('Failed to update role:', err);
            alert(err.message || 'Failed to update user role');
        }
    };

    const isVerified = (user) => Boolean(user.emailVerified ?? user.email_verified);

    const isDeleted = (user) => Boolean(user.deleted);

    const deletedAt = (user) => user.deletedAt || user.deleted_at || null;

    const avatarUrl = (user) => user.avatarUrl || user.avatar_url || null;

    const displayName = (user) => user.displayName || user.display_name || '';

    const getRoleBadgeClass = (role) => {
        switch (role) {
            case 'super_admin': return styles.roleSuperAdmin;
            case 'admin': return styles.roleAdmin;
            default: return styles.roleUser;
        }
    };

    return (
        <div className={styles.users}>
            <div className={styles.header}>
                <h1 className={styles.pageTitle}>Users</h1>
            </div>

            <div className={styles.filters}>
                <input
                    type="text"
                    placeholder="Search by username or email..."
                    value={filters.search}
                    onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value, page: 1 }))}
                    className={styles.searchInput}
                />

                <select
                    value={filters.role}
                    onChange={(e) => setFilters(prev => ({ ...prev, role: e.target.value, page: 1 }))}
                    className={styles.select}
                >
                    <option value="">All Roles</option>
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                    <option value="super_admin">Super Admin</option>
                </select>

                <select
                    value={filters.status}
                    onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value, page: 1 }))}
                    className={styles.select}
                >
                    <option value="all">All Accounts</option>
                    <option value="active">Active Only</option>
                    <option value="deleted">Deleted Only</option>
                </select>
            </div>

            {loading ? (
                <div className={styles.loading}>Loading users...</div>
            ) : users.length === 0 ? (
                <div className={styles.empty}>No users found</div>
            ) : (
                <>
                    <div className={styles.table}>
                        <table>
                            <thead>
                            <tr>
                                <th>User</th>
                                <th>Email</th>
                                <th>Verified</th>
                                <th>Signup</th>
                                <th>Platform</th>
                                <th>Role</th>
                                <th>Founding</th>
                                <th>Coins</th>
                                <th>Quizzes</th>
                                <th>Collections</th>
                                <th>Watchlist</th>
                                <th>Joined</th>
                                <th>Deleted</th>
                                <th>Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            {users.map(user => (
                                <tr key={user.id} className={isDeleted(user) ? styles.deletedRow : undefined}>
                                    <td>
                                        <div className={styles.userCell}>
                                            {avatarUrl(user) ? (
                                                <img src={avatarUrl(user)} alt={user.username} className={styles.avatar} />
                                            ) : (
                                                <div className={styles.avatarPlaceholder}>
                                                    {user.username?.[0]?.toUpperCase() || '?'}
                                                </div>
                                            )}
                                            <div className={styles.userInfo}>
                                                <div className={styles.username}>{user.username}</div>
                                                {displayName(user) && (
                                                    <div className={styles.displayName}>{displayName(user)}</div>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                    <td className={styles.email}>{user.email}</td>
                                    <td>
                                        <span className={`${styles.statusBadge} ${isVerified(user) ? styles.statusYes : styles.statusNo}`}>
                                            {isVerified(user) ? 'Verified' : 'Unverified'}
                                        </span>
                                    </td>
                                    <td>
                                        <SignupMethodBadge user={user} />
                                    </td>
                                    <td>
                                        <PlatformCell user={user} />
                                    </td>
                                    <td>
                                            <span className={`${styles.roleBadge} ${getRoleBadgeClass(user.role)}`}>
                                                {user.role.replace('_', ' ')}
                                            </span>
                                    </td>
                                    <td>
                                        <FoundingToggle
                                            user={user}
                                            onToggle={handleFoundingToggle}
                                        />
                                    </td>
                                    <td>
                                        <div className={styles.statCell}>
                                            <strong>{user.stats?.coins?.total || 0}</strong>
                                            <div className={styles.coinBreakdown}>
                                                {user.stats?.coins?.legendary > 0 && <span className={styles.legendary}>L:{user.stats.coins.legendary}</span>}
                                                {user.stats?.coins?.gold > 0 && <span className={styles.gold}>G:{user.stats.coins.gold}</span>}
                                                {user.stats?.coins?.silver > 0 && <span className={styles.silver}>S:{user.stats.coins.silver}</span>}
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <div className={styles.statCell}>
                                            <strong>{user.stats?.quizzes?.total || 0}</strong>
                                            <span className={styles.statLabel}>
                                                    {user.stats?.quizzes?.passed || 0} passed
                                                </span>
                                        </div>
                                    </td>
                                    <td className={styles.collections}>
                                        {user.stats?.collections?.completed || 0}
                                    </td>
                                    <td>
                                        <WatchlistCell watchlist={user.stats?.watchlist} />
                                    </td>
                                    <td className={styles.date}>
                                        {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '—'}
                                    </td>
                                    <td className={styles.date}>
                                        {isDeleted(user) ? (
                                            <div className={styles.statCell}>
                                                <span className={`${styles.statusBadge} ${styles.statusDeleted}`}>Deleted</span>
                                                <span className={styles.statLabel}>
                                                    {deletedAt(user) ? new Date(deletedAt(user)).toLocaleDateString() : '—'}
                                                </span>
                                            </div>
                                        ) : (
                                            <span className={styles.statLabel}>—</span>
                                        )}
                                    </td>
                                    <td>
                                        <button
                                            onClick={() => handleViewUser(user.id)}
                                            className={styles.viewBtn}
                                        >
                                            View
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>

                    {pagination && pagination.totalPages > 1 && (
                        <div className={styles.pagination}>
                            <button
                                disabled={pagination.page === 1}
                                onClick={() => setFilters(prev => ({ ...prev, page: prev.page - 1 }))}
                                className={styles.pageBtn}
                            >
                                Previous
                            </button>
                            <span className={styles.pageInfo}>
                                Page {pagination.page} of {pagination.totalPages}
                            </span>
                            <button
                                disabled={pagination.page === pagination.totalPages}
                                onClick={() => setFilters(prev => ({ ...prev, page: prev.page + 1 }))}
                                className={styles.pageBtn}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </>
            )}

            {selectedUser && (
                <UserDetailModal
                    userData={selectedUser}
                    onClose={() => setSelectedUser(null)}
                    onRoleChange={handleRoleChange}
                    onFoundingToggle={handleFoundingToggle}
                    // Refresh the panel's stats / recent coins in place after a grant or removal
                    onCoinsChanged={() => handleViewUser(selectedUser.user.id)}
                    onEmailSent={() => handleViewUser(selectedUser.user.id)}
                    isSuperAdmin={isSuperAdmin}
                    navigate={navigate}
                    adminEmail={currentUser?.email || ''}
                />
            )}
        </div>
    );
}

function isFounding(user) {
    return Boolean(user?.isFoundingCollector ?? user?.is_founding_collector);
}

const SIGNUP_METHOD_LABELS = {
    email: 'Email / Password',
    google: 'Google',
    apple: 'Apple',
};

function signupMethodLabel(method) {
    return SIGNUP_METHOD_LABELS[method] || method;
}

function SignupMethodBadge({ user }) {
    const method = user?.signupMethod ?? user?.signup_method ?? null;
    if (!method) {
        return <span className={styles.statLabel}>Unknown</span>;
    }
    const methodClass = {
        email: styles.signupEmail,
        google: styles.signupGoogle,
        apple: styles.signupApple,
    }[method];
    return (
        <span
            className={`${styles.roleBadge} ${methodClass || styles.roleUser}`}
            title={user.signupMethodInferred ? 'Inferred from linked login methods (account predates signup tracking)' : undefined}
        >
            {signupMethodLabel(method)}{user.signupMethodInferred ? '*' : ''}
        </span>
    );
}

const PLATFORM_LABELS = {
    web: 'Web',
    ios: 'iOS',
    android: 'Android',
};

function platformLabel(platform) {
    return PLATFORM_LABELS[platform] || platform;
}

function formatSeen(value) {
    if (!value) return '—';
    const date = new Date(value);
    const days = Math.floor((Date.now() - date.getTime()) / (24 * 60 * 60 * 1000));
    if (days <= 0) return 'today';
    if (days === 1) return 'yesterday';
    if (days < 30) return `${days}d ago`;
    return date.toLocaleDateString();
}

const DEVICE_OS_LABELS = {
    ios: 'iPhone / iPad',
    android: 'Android',
    macos: 'Mac',
    windows: 'Windows',
    chromeos: 'Chromebook',
    linux: 'Linux',
    other: 'Other device',
};

function SignupPlatformBadge({ user }) {
    const platform = user?.signupPlatform ?? user?.signup_platform ?? null;
    const deviceOs = user?.signupDeviceOs ?? user?.signup_device_os ?? null;
    if (!platform) {
        return (
            <span className={styles.statLabel} title="Account predates signup platform tracking">Unknown</span>
        );
    }
    if (platform !== 'web') {
        return <span className={`${styles.roleBadge} ${styles.platformMobile}`}>{platformLabel(platform)} app</span>;
    }
    // Web signup: show the device OS the browser reported, when recorded.
    return (
        <span
            className={`${styles.roleBadge} ${styles.platformWeb}`}
            title={deviceOs ? undefined : 'Device not recorded (web signup before device tracking)'}
        >
            Web{deviceOs ? ` · ${DEVICE_OS_LABELS[deviceOs] || deviceOs}` : ''}
        </span>
    );
}

function AppBadge({ app }) {
    if (!app?.hasApp) {
        return <span className={`${styles.statusBadge} ${styles.statusNo}`}>No app</span>;
    }
    return (
        <span
            className={`${styles.statusBadge} ${styles.statusYes}`}
            title={`Last seen in app: ${app.lastAppSeenAt ? new Date(app.lastAppSeenAt).toLocaleString() : 'unknown'}`}
        >
            App: {app.platforms.map(platformLabel).join(' + ')}
        </span>
    );
}

function PlatformCell({ user }) {
    return (
        <div className={styles.statCell}>
            <span title="Signed up on"><SignupPlatformBadge user={user} /></span>
            <AppBadge app={user?.app} />
            {user?.app?.lastAppSeenAt && (
                <span className={styles.statLabel}>app {formatSeen(user.app.lastAppSeenAt)}</span>
            )}
            {user?.app?.lastWebDeviceOs && (
                <span className={styles.statLabel} title="Device last used on the web app">
                    web on {DEVICE_OS_LABELS[user.app.lastWebDeviceOs] || user.app.lastWebDeviceOs}
                </span>
            )}
        </div>
    );
}

function WatchlistCell({ watchlist }) {
    if (!watchlist?.hasWatchlist) {
        return (
            <div className={styles.statCell}>
                <span className={`${styles.statusBadge} ${styles.statusNo}`}>None</span>
                {watchlist?.customLists > 0 && (
                    <span className={styles.statLabel}>{watchlist.customLists} {watchlist.customLists === 1 ? 'list' : 'lists'}</span>
                )}
            </div>
        );
    }
    return (
        <div className={styles.statCell} title={`${watchlist.saved} saved, ${watchlist.watched} watched`}>
            <strong>{watchlist.titles} {watchlist.titles === 1 ? 'title' : 'titles'}</strong>
            {watchlist.customLists > 0 && (
                <span className={styles.statLabel}>{watchlist.customLists} {watchlist.customLists === 1 ? 'list' : 'lists'}</span>
            )}
        </div>
    );
}

function FoundingToggle({ user, onToggle, showDate = false }) {
    const granted = isFounding(user);
    const grantedAt = user?.founding_collector_granted_at || user?.foundingCollectorGrantedAt;
    return (
        <label className={styles.foundingToggle} title="Founding Collector — cosmetic status label (admin-granted)">
            <input
                type="checkbox"
                checked={granted}
                disabled={user?.deleted}
                onChange={(e) => onToggle(user.id, e.target.checked)}
            />
            <span className={granted ? styles.foundingOn : styles.foundingOff}>
                {granted ? '★ Founding' : '—'}
            </span>
            {showDate && granted && grantedAt && (
                <span className={styles.statLabel}>since {new Date(grantedAt).toLocaleDateString()}</span>
            )}
        </label>
    );
}

const fmtDateTime = (value) => (value ? new Date(value).toLocaleString() : '');

/**
 * One tracked email: subject, type and the furthest outcome reached, with each
 * provider timestamp (Mailgun webhook) in the tooltip.
 */
function EmailHistoryItem({ email, tracking }) {
    const at = (key) => email[key] || email[key.replace(/_([a-z])/g, (_, c) => c.toUpperCase())];
    const createdAt = at('created_at');
    const steps = [
        ['Sent', at('sent_at')],
        ['Delivered', at('delivered_at')],
        ['Opened', at('opened_at')],
        ['Clicked', at('clicked_at')],
        ['Bounced', at('bounced_at')],
        ['Complained', at('complained_at')],
        ['Failed', at('failed_at')],
    ].filter(([, when]) => when);
    const title = [`Queued: ${fmtDateTime(createdAt)}`, ...steps.map(([label, when]) => `${label}: ${fmtDateTime(when)}`)]
        .concat(at('clicked_url') ? [`Clicked link: ${at('clicked_url')}`] : [])
        .join('\n');

    const flags = [];
    if (at('failed_at') || email.status === 'failed') flags.push(['Failed', styles.emailFlagBad]);
    else if (at('bounced_at')) flags.push(['Bounced', styles.emailFlagBad]);
    else if (at('complained_at')) flags.push(['Spam report', styles.emailFlagBad]);
    else if (tracking) {
        flags.push(at('delivered_at') ? ['Delivered', styles.emailFlag] : [at('sent_at') ? 'Sent' : 'Queued', styles.emailFlagMuted]);
        if (at('opened_at')) flags.push(['Opened', styles.emailFlagGood]);
        if (at('clicked_at')) flags.push(['Clicked', styles.emailFlagGood]);
    }

    return (
        <div className={styles.emailItem} title={title}>
            <div className={styles.emailItemMain}>
                <span className={styles.emailItemSubject}>{email.subject || '(no subject)'}</span>
                <span className={styles.itemDate}>
                    {email.label || email.type} · {createdAt ? new Date(createdAt).toLocaleString() : '—'}
                </span>
            </div>
            <div className={styles.emailFlags}>
                {flags.map(([label, cls]) => (
                    <span key={label} className={`${styles.emailFlagBase} ${cls}`}>{label}</span>
                ))}
            </div>
        </div>
    );
}

function UserDetailModal({ userData, onClose, onRoleChange, onFoundingToggle, onCoinsChanged, onEmailSent, isSuperAdmin, navigate, adminEmail }) {
    const { user, recentCoins, recentQuizzes, collections, recentFeedback, recentReports, onboarding, watchlist, recentEmails, emailTracking } = userData;
    const [emailComposerOpen, setEmailComposerOpen] = useState(false);
    const [emailForm, setEmailForm] = useState({
        from: adminEmail,
        subject: 'A note from Aluvo',
        message: '',
    });
    const [emailSending, setEmailSending] = useState(false);
    const [emailError, setEmailError] = useState('');

    const handleSendEmail = async (event) => {
        event.preventDefault();
        setEmailError('');
        try {
            setEmailSending(true);
            await adminApi.sendUserEmail(user.id, emailForm);
            setEmailComposerOpen(false);
            setEmailForm(prev => ({ ...prev, message: '' }));
            alert(`Email sent to ${user.email}`);
            onEmailSent?.();
        } catch (err) {
            setEmailError(err.message || 'Failed to send email');
        } finally {
            setEmailSending(false);
        }
    };

    return (
        <div className={styles.modal} onClick={onClose}>
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <h2>User Detail</h2>
                    <button onClick={onClose} className={styles.closeBtn}>×</button>
                </div>

                <div className={styles.modalBody}>
                    {/* Profile Section */}
                    <section className={styles.section}>
                        <h3 className={styles.sectionTitle}>Profile</h3>
                        <div className={styles.profileInfo}>
                            {(user.avatarUrl || user.avatar_url) ? (
                                <img src={user.avatarUrl || user.avatar_url} alt={user.username} className={styles.avatarLarge} />
                            ) : (
                                <div className={styles.avatarPlaceholderLarge}>
                                    {user.username?.[0]?.toUpperCase() || '?'}
                                </div>
                            )}
                            <div>
                                <div className={styles.detailRow}>
                                    <label>Username:</label>
                                    <span>{user.username}</span>
                                </div>
                                <div className={styles.detailRow}>
                                    <label>Display Name:</label>
                                    <span>{user.displayName || user.display_name || '—'}</span>
                                </div>
                                <div className={styles.detailRow}>
                                    <label>Email:</label>
                                    <span>
                                        {user.email}
                                        {user.email && (
                                            <button
                                                type="button"
                                                onClick={() => setEmailComposerOpen(true)}
                                                className={styles.emailUserLink}
                                            >
                                                Send Email
                                            </button>
                                        )}
                                    </span>
                                </div>
                                <div className={styles.detailRow}>
                                    <label>Role:</label>
                                    {isSuperAdmin ? (
                                        <select
                                            value={user.role}
                                            onChange={(e) => onRoleChange(user.id, e.target.value)}
                                            className={styles.roleSelect}
                                        >
                                            <option value="user">User</option>
                                            <option value="admin">Admin</option>
                                            <option value="super_admin">Super Admin</option>
                                        </select>
                                    ) : (
                                        <span>{user.role.replace('_', ' ')}</span>
                                    )}
                                </div>
                                <div className={styles.detailRow}>
                                    <label>Founding Collector:</label>
                                    <FoundingToggle user={user} onToggle={onFoundingToggle} showDate />
                                </div>
                                <div className={styles.detailRow}>
                                    <label>Joined:</label>
                                    <span>{(user.createdAt || user.created_at) ? new Date(user.createdAt || user.created_at).toLocaleDateString() : '—'}</span>
                                </div>
                                <div className={styles.detailRow}>
                                    <label>Signup Method:</label>
                                    <SignupMethodBadge user={user} />
                                </div>
                                <div className={styles.detailRow}>
                                    <label>Signed Up On:</label>
                                    <SignupPlatformBadge user={user} />
                                </div>
                                <div className={styles.detailRow}>
                                    <label>Mobile App:</label>
                                    <span>
                                        <AppBadge app={user.app} />
                                        {user.app?.appVersion && <span className={styles.statLabel}> v{user.app.appVersion}</span>}
                                    </span>
                                </div>
                                <div className={styles.detailRow}>
                                    <label>Last Seen:</label>
                                    <span>
                                        iOS {formatSeen(user.app?.lastIosSeenAt)} · Android {formatSeen(user.app?.lastAndroidSeenAt)} · Web {formatSeen(user.app?.lastWebSeenAt)}
                                        {user.app?.lastWebDeviceOs && ` (on ${DEVICE_OS_LABELS[user.app.lastWebDeviceOs] || user.app.lastWebDeviceOs})`}
                                    </span>
                                </div>
                                <div className={styles.detailRow}>
                                    <label>Push Notifications:</label>
                                    <span>{user.app?.pushEnabled ? 'Enabled on a device' : 'Not enabled'}</span>
                                </div>
                                {user.loginMethods?.length > 0 && (
                                    <div className={styles.detailRow}>
                                        <label>Login Methods:</label>
                                        <span>{user.loginMethods.map(signupMethodLabel).join(', ')}</span>
                                    </div>
                                )}
                                <div className={styles.detailRow}>
                                    <label>Verified:</label>
                                    <span>{(user.emailVerified ?? user.email_verified) ? 'Yes' : 'No'}</span>
                                </div>
                                {user.deleted && (
                                    <div className={styles.detailRow}>
                                        <label>Deleted:</label>
                                        <span>{(user.deletedAt || user.deleted_at) ? new Date(user.deletedAt || user.deleted_at).toLocaleDateString() : 'Yes'}</span>
                                    </div>
                                )}
                                <div className={styles.detailRow}>
                                    <label>Profile:</label>
                                    <button
                                        onClick={() => {
                                            navigate(`/user/${user.username}`);
                                            onClose();
                                        }}
                                        className={styles.linkBtn}
                                    >
                                        View Public Profile
                                    </button>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Stats Section */}
                    <section className={styles.section}>
                        <h3 className={styles.sectionTitle}>Stats</h3>
                        <div className={styles.statsGrid}>
                            <div className={styles.statBox}>
                                <div className={styles.statLabel}>Total Coins</div>
                                <div className={styles.statValue}>{user.coinCount || user.coin_count || 0}</div>
                            </div>
                            <div className={styles.statBox}>
                                <div className={styles.statLabel}>Quizzes Taken</div>
                                <div className={styles.statValue}>{recentQuizzes?.length || 0}</div>
                            </div>
                            <div className={styles.statBox}>
                                <div className={styles.statLabel}>Collections</div>
                                <div className={styles.statValue}>{collections?.filter(c => c.completed).length || 0}</div>
                            </div>
                            <div className={styles.statBox}>
                                <div className={styles.statLabel}>Followers</div>
                                <div className={styles.statValue}>{user.followersCount || user.followers_count || 0}</div>
                            </div>
                            <div className={styles.statBox}>
                                <div className={styles.statLabel}>Watchlist Titles</div>
                                <div className={styles.statValue}>{watchlist?.titles || 0}</div>
                                {watchlist?.hasWatchlist && (
                                    <div className={styles.statLabel}>{watchlist.saved} saved · {watchlist.watched} watched</div>
                                )}
                            </div>
                            <div className={styles.statBox}>
                                <div className={styles.statLabel}>Custom Lists</div>
                                <div className={styles.statValue}>{watchlist?.customLists || 0}</div>
                            </div>
                        </div>
                    </section>

                    {/* Emails sent to this user, with Mailgun delivery / open / click tracking */}
                    <section className={styles.section}>
                        <h3 className={styles.sectionTitle}>Emails Sent ({recentEmails?.length || 0})</h3>
                        {!emailTracking && recentEmails?.length > 0 && (
                            <div className={styles.itemDate}>
                                Delivery / open / click tracking is off (provider feedback not configured).
                            </div>
                        )}
                        {recentEmails?.length ? (
                            <div className={styles.itemsList}>
                                {recentEmails.map(email => (
                                    <EmailHistoryItem key={email.id} email={email} tracking={emailTracking} />
                                ))}
                            </div>
                        ) : (
                            <div className={styles.itemDate}>No tracked emails yet.</div>
                        )}
                    </section>

                    {/* Intro onboarding selections */}
                    <section className={styles.section}>
                        <h3 className={styles.sectionTitle}>Onboarding Selections</h3>
                        {onboarding ? (
                            <>
                                {onboarding.completedAt && (
                                    <div className={styles.itemDate}>
                                        Completed {new Date(onboarding.completedAt).toLocaleString()}
                                    </div>
                                )}
                                <div className={styles.onboardingLabel}>
                                    Genres ({onboarding.genres?.length || 0})
                                </div>
                                {onboarding.genres?.length > 0 ? (
                                    <div className={styles.chipList}>
                                        {onboarding.genres.map(g => (
                                            <span key={g.id} className={styles.chip}>{g.name}</span>
                                        ))}
                                    </div>
                                ) : (
                                    <div className={styles.emptyNote}>Skipped / none selected</div>
                                )}
                                <div className={styles.onboardingLabel}>
                                    Watched Titles ({onboarding.watchedTitles?.length || 0})
                                </div>
                                {onboarding.watchedTitles?.length > 0 ? (
                                    <div className={styles.itemsList}>
                                        {onboarding.watchedTitles.map(t => (
                                            <div key={`${t.kind}:${t.tmdbId}`} className={styles.item}>
                                                <span className={styles.onboardingTitle}>
                                                    {t.posterUrl ? (
                                                        <img src={t.posterUrl} alt="" className={styles.onboardingPoster} />
                                                    ) : (
                                                        <span className={styles.onboardingPoster} />
                                                    )}
                                                    <span>{t.title}{t.year ? ` (${t.year})` : ''}</span>
                                                </span>
                                                <span className={styles.itemBadge}>{t.kind === 'tv' ? 'TV' : 'Movie'}</span>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className={styles.emptyNote}>None selected</div>
                                )}
                            </>
                        ) : (
                            <div className={styles.emptyNote}>
                                No onboarding selections recorded (user skipped onboarding or signed up before selections were tracked).
                            </div>
                        )}
                    </section>

                    {/* Onboarding diagnostic timeline (admin-only endpoint; no auth data) */}
                    <section className={styles.section}>
                        <h3 className={styles.sectionTitle}>Onboarding Journey</h3>
                        <OnboardingJourney userId={user.id} />
                    </section>

                    {/* Give / remove coins and collection coins */}
                    <UserCoinManager userId={user.id} disabled={user.deleted} onChanged={onCoinsChanged} />

                    {/* Recent Coins */}
                    {recentCoins && recentCoins.length > 0 && (
                        <section className={styles.section}>
                            <h3 className={styles.sectionTitle}>Recent Coins ({recentCoins.length})</h3>
                            <div className={styles.itemsList}>
                                {recentCoins.slice(0, 5).map(coin => (
                                    <div key={coin.id} className={styles.item}>
                                        <span>{coin.badge?.media?.title || coin.collection?.name || '—'}</span>
                                        <span className={styles.itemBadge}>{coin.rarity}</span>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* Recent Feedback */}
                    {recentFeedback && recentFeedback.length > 0 && (
                        <section className={styles.section}>
                            <h3 className={styles.sectionTitle}>Recent Feedback ({recentFeedback.length})</h3>
                            <div className={styles.itemsList}>
                                {recentFeedback.map(fb => (
                                    <div key={fb.id} className={styles.item}>
                                        <span>{fb.type.replace(/_/g, ' ')}</span>
                                        <span className={styles.itemDate}>
                                            {(fb.createdAt || fb.created_at) ? new Date(fb.createdAt || fb.created_at).toLocaleDateString() : '—'}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* Recent Quiz Reports */}
                    {recentReports && recentReports.length > 0 && (
                        <section className={styles.section}>
                            <h3 className={styles.sectionTitle}>Recent Quiz Reports ({recentReports.length})</h3>
                            <div className={styles.itemsList}>
                                {recentReports.map(report => (
                                    <div key={report.id} className={styles.item}>
                                        <span>{report.title || '—'}</span>
                                        <span className={styles.itemBadge}>{report.reason.replace(/_/g, ' ')}</span>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}
                </div>
            </div>
            {emailComposerOpen && (
                <div className={styles.emailComposerOverlay} onClick={(e) => e.stopPropagation()}>
                    <form className={styles.emailComposer} onSubmit={handleSendEmail}>
                        <div className={styles.emailComposerHeader}>
                            <h3>Email @{user.username}</h3>
                            <button
                                type="button"
                                className={styles.closeBtn}
                                onClick={() => setEmailComposerOpen(false)}
                            >
                                ×
                            </button>
                        </div>

                        <label className={styles.emailField}>
                            <span>From</span>
                            <input
                                type="email"
                                value={emailForm.from}
                                onChange={(e) => setEmailForm(prev => ({ ...prev, from: e.target.value }))}
                                required
                            />
                        </label>

                        <label className={styles.emailField}>
                            <span>To</span>
                            <input type="email" value={user.email || ''} disabled />
                        </label>

                        <label className={styles.emailField}>
                            <span>Subject</span>
                            <input
                                type="text"
                                value={emailForm.subject}
                                onChange={(e) => setEmailForm(prev => ({ ...prev, subject: e.target.value }))}
                                maxLength={200}
                                required
                            />
                        </label>

                        <label className={styles.emailField}>
                            <span>Message</span>
                            <textarea
                                value={emailForm.message}
                                onChange={(e) => setEmailForm(prev => ({ ...prev, message: e.target.value }))}
                                rows={9}
                                maxLength={10000}
                                required
                            />
                        </label>

                        {emailError && <div className={styles.emailError}>{emailError}</div>}

                        <div className={styles.emailActions}>
                            <button
                                type="button"
                                className={styles.cancelEmailBtn}
                                onClick={() => setEmailComposerOpen(false)}
                                disabled={emailSending}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className={styles.sendEmailBtn}
                                disabled={emailSending}
                            >
                                {emailSending ? 'Sending...' : 'Send Email'}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}
